import React, { useState, useEffect, useRef } from 'react';
import { QUESTION_BANK, QuestionBankEntry } from '../data/questionBank';
import { WidgetRenderer } from '../widgets/WidgetRenderer';
import { lumiObserver } from '../lumi/observer';
import { localSeenStore, SeenStore } from '../lib/seenStore';

// ── Types ───────────────────────────────────────────────────────────

type ExamProgram = 'naplan' | 'selective_nsw' | 'oc' | 'acer' | 'icas' | 'hsc' | 'vce';

interface Props {
  examProgram: ExamProgram;
  yearId: string;   // e.g. "y5"
  subject: string;  // e.g. "numeracy"
  onClose: () => void;
  seenStore?: SeenStore;  // injectable for testing; defaults to localSeenStore
}

// ── Exam → primitive type filter map ───────────────────────────────

type FilterOption = { label: string; value: string };

const EXAM_FILTERS: Record<ExamProgram, FilterOption[]> = {
  naplan:       [
    { label: 'All',           value: 'all' },
    { label: 'Drag & Drop',   value: 'DragDrop' },
    { label: 'Hot Text',      value: 'HotText2' },
    { label: 'Hot Spot',      value: 'HotSpot' },
    { label: 'Image MCQ',     value: 'ImageMCQ' },
  ],
  selective_nsw: [
    { label: 'All',           value: 'all' },
    { label: 'Fill the Gap',  value: 'InlineCloze' },
    { label: 'Drag & Drop',   value: 'DragDrop' },
    { label: 'Matching',      value: 'ExtractMatching' },
    { label: 'Image MCQ',     value: 'ImageMCQ' },
  ],
  oc: [
    { label: 'All',           value: 'all' },
    { label: 'Fill the Gap',  value: 'InlineCloze' },
    { label: 'Drag & Drop',   value: 'DragDrop' },
    { label: 'Matching',      value: 'ExtractMatching' },
    { label: 'Image MCQ',     value: 'ImageMCQ' },
  ],
  acer: [
    { label: 'All',           value: 'all' },
    { label: 'Image MCQ',     value: 'ImageMCQ' },
  ],
  icas: [
    { label: 'All',           value: 'all' },
    { label: 'Drag & Drop',   value: 'DragDrop' },
    { label: 'Hot Spot',      value: 'HotSpot' },
    { label: 'Gap Fill',      value: 'GapMatch' },
    { label: 'Image MCQ',     value: 'ImageMCQ' },
  ],
  hsc: [{ label: 'All', value: 'all' }, { label: 'Exam Mode', value: 'ExamMode' }],
  vce: [
    { label: 'All',           value: 'all' },
    { label: 'Exam Mode',     value: 'ExamMode' },
  ],
};

const EXAM_LABELS: Record<ExamProgram, string> = {
  naplan:       'NAPLAN',
  selective_nsw:'Selective',
  oc:           'OC',
  acer:         'ACER',
  icas:         'ICAS',
  hsc:          'HSC',
  vce:          'VCE',
};

// ── Helpers ─────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Derive examProgram from entry id prefix for overnight-generated entries
function inferExamProgram(q: QuestionBankEntry): string {
  if (!q) return '';
  if (q.examProgram) return q.examProgram;
  const id = q.id || '';
  if (id.startsWith('hsc_')) return 'hsc';
  if (id.startsWith('vce_')) return 'vce';
  return '';
}

function inferYearId(q: QuestionBankEntry): string {
  if (q.yearId) return q.yearId;
  const id = q.id || '';
  const m = id.match(/_(y\d+)_/);
  return m ? m[1] : '';
}

// ── pickQuestions with anti-repeat memory ───────────────────────────

interface PickResult {
  questions: QuestionBankEntry[];
  bankSize: number;
  freshCount: number;  // how many were unseen
}

/** How many interactive questions exist for one test, year and subject (the Interactive button hides below a handful). */
export function interactiveCount(examProgram: string, yearId: string, subject: string): number {
  return QUESTION_BANK.filter(q => q && inferExamProgram(q) === examProgram && (!yearId || inferYearId(q) === yearId) && q.subject === subject).length;
}

function pickQuestions(
  examProgram: ExamProgram,
  yearId: string,
  subject: string,
  filterType: string,
  count = 10,
  seenStore: SeenStore = localSeenStore,
): PickResult {
  // Build full pool for this scope
  let pool = QUESTION_BANK.filter(q => {
    if (!q) return false;
    if (inferExamProgram(q) !== examProgram) return false;
    if (yearId && inferYearId(q) !== yearId) return false;
    if (subject && q.subject !== subject) return false;
    if (filterType !== 'all' && q.primitiveType !== filterType) return false;
    return true;
  });
  // Relax year constraint if empty
  if (pool.length === 0) {
    pool = QUESTION_BANK.filter(q => {
      if (!q) return false;
      if (inferExamProgram(q) !== examProgram) return false;
      if (subject && q.subject !== subject) return false;
      if (filterType !== 'all' && q.primitiveType !== filterType) return false;
      return true;
    });
  }

  const bankSize = pool.length;

  if (bankSize === 0) {
    return { questions: [], bankSize: 0, freshCount: 0 };
  }

  if (bankSize < count) {
    console.warn(`[pickQuestions] Subject has only ${bankSize} questions, requested ${count}`);
    return { questions: shuffle(pool), bankSize, freshCount: bankSize };
  }

  const scope = { examProgram, year: yearId, subject, mode: filterType };
  const seenIds = new Set(seenStore.getSeenIds(scope));
  const available = pool.filter(q => !seenIds.has(q.id));

  let chosen: QuestionBankEntry[];

  if (available.length >= count) {
    // Plenty of fresh questions — sample from unseen only
    chosen = shuffle(available).slice(0, count);
  } else {
    const exhaustionRatio = available.length / bankSize;
    if (exhaustionRatio < 0.2) {
      // ≥80% exhausted — use all remaining fresh + fill from oldest seen
      const seenEntries = seenStore.getSeenWithTimestamps(scope)
        .sort((a, b) => a.timestamp - b.timestamp); // oldest first
      const seenById = new Map(seenEntries.map(e => [e.id, e.timestamp]));
      const seenPool = pool
        .filter(q => seenIds.has(q.id))
        .sort((a, b) => (seenById.get(a.id) ?? 0) - (seenById.get(b.id) ?? 0));
      const needed = count - available.length;
      chosen = [...shuffle(available), ...seenPool.slice(0, needed)];
    } else {
      // <80% exhausted but not enough fresh — return all available
      chosen = shuffle(available);
    }
  }

  return { questions: chosen, bankSize, freshCount: available.length };
}

// ── Main component ───────────────────────────────────────────────────

export function ExamInteractive({ examProgram, yearId, subject, onClose, seenStore = localSeenStore }: Props) {
  const [filterType, setFilterType] = useState('all');
  const [session, setSession] = useState<QuestionBankEntry[]>([]);
  const [bankSize, setBankSize] = useState(0);
  const [freshCount, setFreshCount] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const sessionId = useRef(`ei_${Date.now()}`);
  // Track which question IDs were actually rendered this session
  const renderedIds = useRef<string[]>([]);

  const filterOptions = EXAM_FILTERS[examProgram] ?? EXAM_FILTERS.naplan;
  const examLabel = EXAM_LABELS[examProgram] ?? examProgram.toUpperCase();
  const scope = { examProgram, year: yearId, subject, mode: filterType };

  // Build session when filter changes
  useEffect(() => {
    console.log('[ExamInteractive] mount', { examProgram, yearId, subject, filterType });
    const result = pickQuestions(examProgram, yearId, subject, filterType, 10, seenStore);
    console.log('[ExamInteractive] pool size', result.questions.length, result.questions.map(q => `${q.id}(${q.primitiveType})`));
    setSession(result.questions);
    setBankSize(result.bankSize);
    setFreshCount(result.freshCount);
    setQIdx(0);
    setAnswered(false);
    setDone(false);
    setScore({ correct: 0, total: 0 });
    renderedIds.current = [];
  }, [examProgram, yearId, subject, filterType]);

  // Mark each question as seen when it becomes the current question
  useEffect(() => {
    const q = session[qIdx];
    if (!q) return;
    if (!renderedIds.current.includes(q.id)) {
      renderedIds.current = [...renderedIds.current, q.id];
      seenStore.markSeen(scope, [q.id]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIdx, session]);

  // Listen for widget answers via the real lumiObserver singleton
  useEffect(() => {
    const id = sessionId.current;
    const ANSWER_EVENTS = ['widget_answered', 'attempt_succeeded', 'attempt_failed', 'exam_submitted'];
    const unsubscribe = lumiObserver.subscribe((evt) => {
      if (evt.lessonId !== id) return;
      if (!ANSWER_EVENTS.includes(evt.type)) return;
      setAnswered(true);
      const isCorrect = evt.type === 'attempt_succeeded' || evt.payload?.correct === true;
      if (isCorrect) {
        setScore(s => ({ correct: s.correct + 1, total: s.total + 1 }));
      } else {
        setScore(s => ({ ...s, total: s.total + 1 }));
      }
    });
    return unsubscribe;
  }, []);

  function handleNext() {
    if (qIdx + 1 >= session.length) {
      setDone(true);
    } else {
      setQIdx(i => i + 1);
      setAnswered(false);
    }
  }

  function handleRestart() {
    const result = pickQuestions(examProgram, yearId, subject, filterType, 10, seenStore);
    setSession(result.questions);
    setBankSize(result.bankSize);
    setFreshCount(result.freshCount);
    setQIdx(0);
    setAnswered(false);
    setDone(false);
    setScore({ correct: 0, total: 0 });
    renderedIds.current = [];
  }

  function handleResetProgress() {
    seenStore.reset(scope);
    setShowResetConfirm(false);
    handleRestart();
  }

  const current = session[qIdx];
  const pct = session.length > 0 ? Math.round(((qIdx + (done ? 1 : 0)) / session.length) * 100) : 0;

  // ── Styles ───────────────────────────────────────────────────────

  const s = {
    root: { minHeight: '100%', background: '#FAFAF6', fontFamily: "'Plus Jakarta Sans', sans-serif" } as React.CSSProperties,
    header: { padding: '16px 20px 12px', borderBottom: '1px solid #E5E0D8', display: 'flex', alignItems: 'center', gap: 10 } as React.CSSProperties,
    backBtn: { background: 'none', border: 'none', color: '#6B7280', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '0 4px' } as React.CSSProperties,
    title: { flex: 1, fontSize: 15, fontWeight: 700, color: '#1A1A1A', margin: 0 } as React.CSSProperties,
    filterRow: { padding: '12px 20px', display: 'flex', gap: 8, overflowX: 'auto' as const, borderBottom: '1px solid #E5E0D8' },
    filterBtn: (active: boolean): React.CSSProperties => ({
      padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer',
      fontFamily: "'Plus Jakarta Sans', sans-serif", border: 'none', whiteSpace: 'nowrap',
      background: active ? '#0D9488' : '#F3F4F6',
      color: active ? '#fff' : '#374151',
    }),
    progressBar: { height: 4, background: '#E5E0D8', borderRadius: 2, margin: '0 20px 0' } as React.CSSProperties,
    progressFill: { height: '100%', background: '#0D9488', borderRadius: 2, transition: 'width 0.3s ease', width: `${pct}%` } as React.CSSProperties,
    progressText: { padding: '8px 20px 0', fontSize: 11, color: '#9CA3AF', fontWeight: 600 } as React.CSSProperties,
    body: { padding: '20px 20px 100px' } as React.CSSProperties,
    nextBtn: {
      position: 'fixed' as const, bottom: 20, left: '50%', transform: 'translateX(-50%)',
      padding: '13px 40px', background: 'linear-gradient(135deg, #0D9488, #0F766E)',
      border: 'none', borderRadius: 99, color: '#fff', fontSize: 14, fontWeight: 700,
      cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: '0 4px 14px #0D948844',
    },
    doneCard: { padding: 32, textAlign: 'center' as const },
    emptyCard: { padding: 40, textAlign: 'center' as const, color: '#9CA3AF', fontSize: 14 },
  };

  // ── Coming-soon guard for subjects with no questions yet ─────────

  const COMING_SOON_SUBJECTS: Record<ExamProgram, string[]> = {
    hsc:          [],
    vce:          [],
    naplan:       [],
    selective_nsw:[],
    oc:           [],
    acer:         [],
    icas:         [],
  };

  const COMING_SOON_COPY: Partial<Record<string, string>> = {
    english_advanced: `HSC English Advanced practice is being hand-curated from real NESA past papers. Live next week.`,
    physics:          `HSC Physics extended-response practice is being prepared. Live next week.`,
    english:          `VCE English practice is being hand-curated from real VCAA past papers. Live next week.`,
    biology:          `VCE Biology extended-response practice is being prepared. Live next week.`,
  };

  const isOwnerMode = (() => {
    try { const a = JSON.parse(localStorage.getItem('lumio_access') || '{}'); return a.plan === 'owner'; } catch { return false; }
  })();

  const showComingSoon =
    !isOwnerMode && (COMING_SOON_SUBJECTS[examProgram] ?? []).includes(subject);

  if (showComingSoon) {
    const copy = COMING_SOON_COPY[subject] ?? `${examLabel} practice for this subject is coming soon.`;
    return (
      <div style={s.root}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={onClose}>←</button>
          <p style={s.title}>Interactive Practice — {examLabel}</p>
        </div>
        <div style={{ padding: '48px 32px', textAlign: 'center' as const }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>📋</div>
          <p style={{ fontSize: 18, fontWeight: 800, color: '#1A1A1A', margin: '0 0 12px' }}>
            Coming soon
          </p>
          <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 8px', lineHeight: 1.6, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
            {copy}
          </p>
        </div>
      </div>
    );
  }

  // ── Completion screen ────────────────────────────────────────────

  if (done) {
    const pctScore = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
    const emoji = pctScore >= 80 ? '🎉' : pctScore >= 60 ? '👍' : '📚';
    return (
      <div style={s.root}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={onClose}>←</button>
          <p style={s.title}>Interactive Practice — {examLabel}</p>
        </div>
        <div style={s.doneCard}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>{emoji}</div>
          <p style={{ fontSize: 22, fontWeight: 800, color: '#1A1A1A', margin: '0 0 8px' }}>
            {score.correct} / {score.total} Correct
          </p>
          <p style={{ fontSize: 14, color: '#6B7280', margin: '0 0 28px' }}>
            {pctScore >= 80 ? 'Excellent work!' : pctScore >= 60 ? 'Good effort — keep practising.' : 'Keep going — you\'ll improve!'}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleRestart} style={{ padding: '12px 28px', background: '#0D9488', border: 'none', borderRadius: 99, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Try Again →
            </button>
            <button onClick={onClose} style={{ padding: '12px 28px', background: '#F3F4F6', border: 'none', borderRadius: 99, color: '#374151', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Back to Subjects
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────

  if (session.length === 0) {
    return (
      <div style={s.root}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={onClose}>←</button>
          <p style={s.title}>Interactive Practice — {examLabel}</p>
        </div>
        <div style={s.filterRow}>
          {filterOptions.map(opt => (
            <button key={opt.value} style={s.filterBtn(filterType === opt.value)} onClick={() => setFilterType(opt.value)}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={s.emptyCard}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🚧</div>
          <p style={{ fontWeight: 700, color: '#1A1A1A', marginBottom: 6 }}>Questions coming soon</p>
          <p style={{ margin: 0 }}>{isOwnerMode ? `No questions in this slice yet (owner view — ${examLabel} / ${subject}).` : `More interactive questions for ${examLabel} are being added. Try a different filter or check back soon.`}</p>
        </div>
      </div>
    );
  }

  // ── Main question view ────────────────────────────────────────────

  // Availability strip: show bank stats
  const seenCount = seenStore.getSeenIds(scope).filter(id =>
    QUESTION_BANK.some(q => q?.id === id && q.subject === subject)
  ).length;
  const availableNow = Math.max(0, bankSize - seenCount);

  return (
    <div style={s.root}>
      {/* Header */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onClose}>←</button>
        <p style={s.title}>Interactive Practice — {examLabel}</p>
        <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>
          {qIdx + 1} / {session.length}
        </span>
      </div>

      {/* Filter pills */}
      <div style={s.filterRow}>
        {filterOptions.map(opt => (
          <button key={opt.value} style={s.filterBtn(filterType === opt.value)} onClick={() => setFilterType(opt.value)}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Availability strip */}
      {bankSize > 0 && (
        <div style={{ padding: '6px 20px', background: '#F0FDF9', borderBottom: '1px solid #D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>
            {availableNow} / {bankSize} questions fresh
          </span>
          {showResetConfirm ? (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#6B7280' }}>Reset seen history?</span>
              <button onClick={handleResetProgress} style={{ fontSize: 11, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0 }}>Yes</button>
              <button onClick={() => setShowResetConfirm(false)} style={{ fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>No</button>
            </span>
          ) : (
            <button onClick={() => setShowResetConfirm(true)} style={{ fontSize: 10, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Reset progress
            </button>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div style={s.progressBar}><div style={s.progressFill} /></div>
      <p style={s.progressText}>Question {qIdx + 1} of {session.length}</p>

      {/* Widget */}
      <div style={s.body}>
        {current && (
          <>
            {console.log('[ExamInteractive] current question', { id: current.id, type: current.primitiveType, configKeys: Object.keys(current.config || {}) }) as unknown as null}
            <WidgetRenderer
              key={current.id}
              widget={{ type: current.primitiveType, config: current.config }}
              lessonId={sessionId.current}
              mode="quiz"
            />
          </>
        )}
      </div>

      {/* Next button */}
      {answered && (
        <button style={s.nextBtn} onClick={handleNext}>
          {qIdx + 1 >= session.length ? 'See Results →' : 'Next Question →'}
        </button>
      )}
    </div>
  );
}
